import { Box } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { getItemsByMenuId } from "../../services/itemService";
import { getMenus } from "../../services/menuService";
import { isImageUrl } from "../../utils/isImageUrl";
import ButtonsGroup from "./buttonsGroup";
import LoginAdminModel from "./loginAdminModel";
import MenuButton from "./menuButton";
import RestaurantLogo from "./restaurantLogo";
import EmailLoginModel from "./EmailLoginMode";

const imageCache = {
  loaded: 0,
  total: 0,
  images: [],
  isComplete: false,
};

function preloadImages(imgs, onProgress, onComplete) {
  imageCache.total = imgs.length;
  imageCache.loaded = 0;
  imageCache.isComplete = false;

  if (imgs.length === 0) {
    imageCache.isComplete = true;
    onComplete?.();
    return;
  }

  imgs.forEach((imgSrc, index) => {
    const img = new Image();
    imageCache.images.push(img);

    img.onload = () => {
      imageCache.loaded++;
      const progress = Math.round((imageCache.loaded / imageCache.total) * 100);
      onProgress?.(progress, imageCache.loaded, imageCache.total);

      if (imageCache.loaded === imageCache.total) {
        imageCache.isComplete = true;
        onComplete?.();
      }
    };

    img.onerror = () => {
      imageCache.loaded++;
      const progress = Math.round((imageCache.loaded / imageCache.total) * 100);
      onProgress?.(progress, imageCache.loaded, imageCache.total);

      if (imageCache.loaded === imageCache.total) {
        imageCache.isComplete = true;
        onComplete?.();
      }
    };

    img.src = imgSrc;
  });
}

const Startup = () => {
  const [isInView, setIsInView] = useState(false);
  const [isAdminLoginOpen, setIsAdminLoginOpen] = useState(false);
  const [isEmailLoginOpen, setIsEmailLoginOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const {
    loginAdminUser,
    splashScreenBg,
    requestEmailVerification,
    verifyEmailCode,
  } = useAuth();
  const ref = useRef();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingProgress(0);
        const response = await getMenus();
        const promises = response.data.map(async (menu) => {
          const items = await getItemsByMenuId(menu.id);
          return { menu, items };
        });
        const data = await Promise.all(promises);
        const imgs = new Set();
        data.forEach(({ items }) => {
          items.data.forEach((item) => {
            if (item.image && isImageUrl(item.image)) {
              imgs.add(item.image);
            }
            if (item.categories) {
              item.categories.forEach((category) => {
                if (category.image && isImageUrl(category.image)) {
                  imgs.add(category.image);
                }
              });
            }
          });
        });
        preloadImages(Array.from(imgs), (progress, loaded, total) => {
          setLoadingProgress(progress);
        });
      } catch (error) {
        setError("Failed to load data");
      }
    };
    fetchData();
  }, []);

  const handleOpenAdminLogin = () => setIsAdminLoginOpen(true);
  const handleCloseAdminLogin = () => setIsAdminLoginOpen(false);

  const handleOpenEmailLogin = () => setIsEmailLoginOpen(true);
  const handleCloseEmailLogin = () => setIsEmailLoginOpen(false);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await loginAdminUser(username, password);
    } catch (error) {
      setError(error.message || "Login failed. Please try again.");
    }
    if (!error) handleCloseAdminLogin();
  };

  const handleEmailVerificationRequest = async (email) => {
    setError(null);
    try {
      await requestEmailVerification(email);
      return true;
    } catch (error) {
      setError(error.message || "Failed to send verification code.");
      return false;
    }
  };

  const handleEmailLogin = async (email, code) => {
    setError(null);
    try {
      await verifyEmailCode(email, code);
      handleCloseEmailLogin();
    } catch (error) {
      setError(error.message || "Invalid verification code.");
    }
  };

  useEffect(() => {
    const refValue = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold: 0.1 }
    );
    if (refValue) observer.observe(refValue);
    return () => {
      if (refValue) observer.unobserve(refValue);
    };
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        height: "100svh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-around",
        alignItems: "center",
        textAlign: "center",
        backgroundColor: isImageUrl(splashScreenBg)
          ? "transparent"
          : splashScreenBg,
        backgroundImage:
          isInView && isImageUrl(splashScreenBg)
            ? `url(${splashScreenBg})`
            : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
        transition: "background-image 0.5s ease-in-out",
        position: "relative",
      }}
    >
      <RestaurantLogo />
      <MenuButton loadingProgress={loadingProgress} />
      <ButtonsGroup handleOpenAdminLogin={handleOpenEmailLogin} />
      {/* <LoginAdminModel
        handleCloseAdminLogin={handleCloseAdminLogin}
        isAdminLoginOpen={isAdminLoginOpen}
        handleAdminLogin={handleAdminLogin}
        username={username}
        setUsername={setUsername}
        password={password}
        setPassword={setPassword}
        error={error}
      />  */}

      <EmailLoginModel
        handleCloseEmailLogin={handleCloseEmailLogin}
        isEmailLoginOpen={isEmailLoginOpen}
        handleEmailVerificationRequest={handleEmailVerificationRequest}
        handleEmailLogin={handleEmailLogin}
        error={error}
      />
    </Box>
  );
};

export default Startup;
